<template>
	<BaseModal :show="show" z-index="z-99999">
		<form class="flex flex-col h-fit sm:w-[600px] md:w-[745px] md:max-w-[745px] bg-neutral">
			<!-- Prevent implicit submission of the form -->
			<button type="submit" disabled style="display: none" aria-hidden="true"></button>

			<div class="flex flex-row align-middle">
				<p class="text-xl font-bold leading-7 text-heading">{{ organization_group_id ? 'Edit ': 'Add '}} Organization Group</p>
				<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="ml-auto cursor-pointer" @click="cancel" />
			</div>

			<div class="flex flex-col mt-4">
				<InputText class="flex-1" label="Organization Group Name*" type="text" input-height="h-11" font-weight="font-normal" v-model="organization_group_name" error-field="name" :errors="error" />
			</div>

			<div>
				<div class="flex flex-row mt-3">
					<span class="my-auto mr-auto whitespace-nowrap">Associated Organizations*</span>

					<EndpointCombobox class="w-full my-auto ml-8" id="organizations" v-model="selectedOrganization" placeholder="Enter organization name"
														label-text-size="text-sm" input-height="h-10" icon="edit" max-search-results-height="max-h-30"
														:enterable="true" :endpoint="`organizations/organizations/search-by-name?name=`"/>

					<SubmitButton class="my-auto ml-3" label="Add" :size="ButtonSize.ExtraSmall" @click="addOrganization" :disabled="!selectedOrganization" />
				</div>

				<PortalDataTable
					:server-mode="false"
					:endpoints="endpoints"
					:row-data="organizations"
				>
					<template #name="data">
						<span class="h-11 w-full whitespace-normal px-2 border border-neutral-subtle">{{ data.value.organization_name + (data.value.organization_name_dba ? " (" + data.value.organization_name_dba + ")" : "") }}</span>
					</template>

					<template #delete="data">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="fill-current hover:fill-icon-primary-hover cursor-pointer" @click="removeOrganization(data.value.id)">
							<title>Delete</title>
							<path d="M18.3375 3.30001H15.45V2.66251C15.45 1.53751 14.55 0.637512 13.425 0.637512H10.5375C9.41251 0.637512 8.51251 1.53751 8.51251 2.66251V3.30001H5.62501C4.53751 3.30001 3.63751 4.20001 3.63751 5.28751V6.41251C3.63751 7.23751 4.12501 7.91251 4.83751 8.21251L5.43751 21.1875C5.51251 22.425 6.48751 23.3625 7.72501 23.3625H16.1625C17.4 23.3625 18.4125 22.3875 18.45 21.1875L19.125 8.17501C19.8375 7.87501 20.325 7.16251 20.325 6.37501V5.25001C20.325 4.20001 19.425 3.30001 18.3375 3.30001ZM10.2375 2.66251C10.2375 2.47501 10.3875 2.32501 10.575 2.32501H13.4625C13.65 2.32501 13.8 2.47501 13.8 2.66251V3.30001H10.275V2.66251H10.2375ZM5.36251 5.28751C5.36251 5.13751 5.47501 4.98751 5.66251 4.98751H18.3375C18.4875 4.98751 18.6375 5.10001 18.6375 5.28751V6.41251C18.6375 6.56251 18.525 6.71251 18.3375 6.71251H5.66251C5.51251 6.71251 5.36251 6.60001 5.36251 6.41251V5.28751ZM16.2 21.675H7.80001C7.46251 21.675 7.20001 21.4125 7.20001 21.1125L6.60001 8.40001H17.4375L16.8375 21.1125C16.8 21.4125 16.5375 21.675 16.2 21.675Z" fill=""/>
							<path d="M12 12.15C11.55 12.15 11.1375 12.525 11.1375 13.0125V17.775C11.1375 18.225 11.5125 18.6375 12 18.6375C12.45 18.6375 12.8625 18.2625 12.8625 17.775V13.0125C12.8625 12.525 12.45 12.15 12 12.15Z" fill=""/>
							<path d="M15 12.9C14.5125 12.8625 14.1375 13.2 14.1 13.6875L13.875 16.9875C13.8375 17.4375 14.175 17.85 14.6625 17.8875C14.7 17.8875 14.7 17.8875 14.7375 17.8875C15.1875 17.8875 15.5625 17.55 15.5625 17.1L15.7875 13.8C15.7875 13.3125 15.45 12.9375 15 12.9Z" fill=""/>
							<path d="M8.96251 12.9C8.51251 12.9375 8.13751 13.35 8.17501 13.8L8.43751 17.1C8.47501 17.55 8.85001 17.8875 9.26251 17.8875C9.30001 17.8875 9.30001 17.8875 9.33751 17.8875C9.78751 17.85 10.1625 17.4375 10.125 16.9875L9.86251 13.6875C9.86251 13.2 9.45001 12.8625 8.96251 12.9Z" fill=""/>
						</svg>
					</template>
				</PortalDataTable>
			</div>

			<div class="flex w-full mt-6">
				<div class="flex-grow">
					<SubmitButton v-if="organization_group_id" label="Delete" :type="ButtonType.Warning" :size="ButtonSize.Small"
												:disabled="isSaving || isDeleting" :loading="isDeleting" @click="onDeleteClicked" />
				</div>

				<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" :disabled="isSaving || isDeleting" class="ml-auto mr-3" @click="cancel" />
				<SubmitButton label="Save" :size="ButtonSize.Small" :loading="isSaving" @click="saveClicked" :disabled="disableSaveButton" />
			</div>
		</form>
	</BaseModal>

	<Teleport to="html">
		<MultiButtonModal
			:show="showDiscardChangesModal"
			icon="alert"
			title="Discard Changes"
			message="Are you sure? Any unsaved changes will be lost."
			:buttons="[
									new ModalButtonData(
										'Cancel', () => { showDiscardChangesModal = false }, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { emit('dismiss') }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>

	<Teleport to="html">
		<MultiButtonModal
			:show="showDeleteModal"
			icon="alert"
			title="Delete Organization Group"
			message="Are you sure?<br><br>This cannot be undone."
			:buttons="[
									new ModalButtonData(
										'Cancel', () => { showDeleteModal = false }, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										'Delete', () => { deleteOrganizationGroup() }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>
</template>

<script setup lang="js">
import InputText from "@/components/Forms/InputText.vue"
import { computed, onMounted, ref } from 'vue'
import SubmitButton from "@/components/Forms/Buttons/SubmitButton.vue"
import { ButtonSize, ButtonType } from "@/components/Forms/Buttons/SubmitButtonConstants.js"
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import { organizationGroupsApi } from '@/api/Organizations/OrganizationGroupsApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import EndpointCombobox from '@/components/Forms/EndpointCombobox.vue'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'

const props = defineProps({
	show: { type: Boolean, default: false },
	organizationGroupToEdit: { type: Object, default: null },
})

const isSaving = ref(false)
const isDeleting = ref(false)
const error = ref(null)
const organization_group_id = ref(null)
const organization_group_name = ref(null)
const organizations = ref([])
const showDiscardChangesModal = ref(false)
const showDeleteModal = ref(false)
const disableSaveButton = computed(() => {
	return isSaving.value || isDeleting.value || !organization_group_name.value || organizations.value.length === 0
})
const toaster = new Toaster()
const selectedOrganization = ref(null)

const emit = defineEmits(['dismiss'])

const columns = computed(() => {
	return [
		{ field: "id", hide: true },
		{ field: "name", title: "Organizations", sort: false },
		{ field: "delete", title: "", sort: false, width: '1%' },
	]
})

const endpoints = [
	new DataTableEndpointDefinition({
		enableSearch: false,
		initialSortColumn: 'id',
		columns: columns,
	})
]

onMounted(() => {
	handleOrganizationGroup(props.organizationGroupToEdit)
})

function handleOrganizationGroup(organizationGroupToEdit) {
	if (organizationGroupToEdit) {
		organization_group_id.value = organizationGroupToEdit.id
		organization_group_name.value = organizationGroupToEdit.name

		if (organizationGroupToEdit.organizations) {
			organizations.value = JSON.parse(JSON.stringify(organizationGroupToEdit.organizations))
		}
	}
}

function addOrganization() {
	if(selectedOrganization.value != null) {
		const exists = organizations.value.find((organization) => organization.id === parseInt(selectedOrganization.value.id))

		if (!exists) {
			organizations.value.push({
				'id': selectedOrganization.value.id,
				'organization_name': selectedOrganization.value.data.organization_name,//name
				'organization_name_dba': selectedOrganization.value.data.organization_name_dba
			})
		}

		selectedOrganization.value = null
	}
}

function removeOrganization(organizationId) {
	const index = organizations.value.findIndex(org => org.id === organizationId);
	if (index !== -1) {
		organizations.value.splice(index, 1);
	}
}

function cancel() {
	if (changesMade()) {
		showDiscardChangesModal.value = true
	} else {
		emit("dismiss")
	}
}

function changesMade() {
	let oldName = props.organizationGroupToEdit.name
	const oldOrgIds = props.organizationGroupToEdit.organizations?.map((organization) => { return organization.id}) ?? []
	const currentOrgIds = organizations.value.map((organization) => { return organization.id})
	const hasOrgs = oldOrgIds.length !== 0 || currentOrgIds.length !== 0
	const orgIdsMatch = hasOrgs && oldOrgIds.length === currentOrgIds.length && oldOrgIds.every((value, index) => value === currentOrgIds[index])

	return (organization_group_name.value !== null && organization_group_name.value !== oldName) || (hasOrgs && !orgIdsMatch)
}

function saveClicked() {
	if (!changesMade()) {
		cancel()
		return
	}

	error.value = null
	isSaving.value = true

	const successCallback = () =>
	{
		isSaving.value = false
		emit('dismiss', true)
	}

	const errorCallback = (e) =>
	{
		isSaving.value = false
		error.value = e.showErrorBanner()
	}

	const organizationIds = organizations.value.map((organization) => { return organization.id})

	if (organization_group_id.value == null) {
		organizationGroupsApi.create({
			'organization_group': {
				'name': organization_group_name.value,
				'organization_ids': organizationIds
			}
		}, successCallback, errorCallback)
	}
	else {
		organizationGroupsApi.update({
			'organization_group': {
				'id': organization_group_id.value,
				'name': organization_group_name.value,
				'organization_ids': organizationIds
			}
		}, successCallback, errorCallback)
	}
}

function onDeleteClicked() {
	showDeleteModal.value = true
}

function deleteOrganizationGroup() {
	error.value = null
	isDeleting.value = true

	organizationGroupsApi.delete(props.organizationGroupToEdit.id,
		(success) => {
			error.value = null
			isDeleting.value = false

			if (success) {
				toaster.success('Organization Group successfully deleted!')
				emit('dismiss', true)
			}	else {
				toaster.error('Section failed to delete. Please try again.')
			}
		})
}
</script>